import { makeObservable, action } from 'mobx';
import { Tool, type ToolContext } from './base/Tool';
import type {
  LineElement,
  Element as CanvasElement,
  ElementConnection,
} from '@/types';
import { findNearestElement } from '@/utils/geometry/connections';
import { calculateMiddleControlPoint } from '@/utils/bezier';

export class LineTool extends Tool {
  private startConnection: ElementConnection | null = null;
  private endConnection: ElementConnection | null = null;

  constructor() {
    super('line', 'Line');
    makeObservable(this, {
      onStart: action,
      onMove: action,
      onEnd: action,
    });
  }

  onStart(context: ToolContext): CanvasElement | null {
    const { rootStore, startPoint, elements } = context;
    rootStore.uiStore.startDrawing(startPoint);

    // Check for connection at start point
    const connection = findNearestElement(startPoint, elements || []);
    if (connection && rootStore.uiStore.startPoint) {
      this.startConnection = {
        elementId: connection.element.id,
        pointId: connection.connectionPoint.id,
      };
      // Snap start point
      rootStore.uiStore.startPoint.x = connection.connectionPoint.x;
      rootStore.uiStore.startPoint.y = connection.connectionPoint.y;
    } else {
      this.startConnection = null;
    }

    return null;
  }

  onMove(context: ToolContext): CanvasElement | null {
    const { rootStore, currentPoint, elements } = context;
    if (!rootStore.uiStore.isDrawing || !rootStore.uiStore.startPoint) {
      return null;
    }

    let finalPoint = currentPoint;

    // Check for connection at current point (end point)
    const connection = findNearestElement(currentPoint, elements || []);
    if (connection) {
      this.endConnection = {
        elementId: connection.element.id,
        pointId: connection.connectionPoint.id,
      };
      // Snap end point
      finalPoint = {
        x: connection.connectionPoint.x,
        y: connection.connectionPoint.y,
      };
    } else {
      this.endConnection = null;
    }

    return {
      id: this.generatePreviewId(),
      type: 'line',
      x: 0,
      y: 0,
      points: [rootStore.uiStore.startPoint, finalPoint],
      style: { ...context.persistentStyle },
      isSelected: false,
      zIndex: 0,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    } as LineElement;
  }

  onEnd(context: ToolContext): CanvasElement | null {
    const { rootStore, currentPoint, elements } = context;
    const { uiStore, projectStore } = rootStore;

    if (
      !uiStore.isDrawing ||
      !uiStore.startPoint ||
      !this.validateElement({
        ...context,
        startPoint: uiStore.startPoint,
        currentPoint,
      })
    ) {
      uiStore.endDrawing();
      this.resetToolState();
      return null;
    }

    let finalPoint = currentPoint;
    const endConnection = findNearestElement(currentPoint, elements || []);
    if (endConnection) {
      this.endConnection = {
        elementId: endConnection.element.id,
        pointId: endConnection.connectionPoint.id,
      };
      finalPoint = {
        x: endConnection.connectionPoint.x,
        y: endConnection.connectionPoint.y,
      };
    }

    const controlPoint = calculateMiddleControlPoint(
      uiStore.startPoint,
      finalPoint
    );

    projectStore.createElement({
      type: 'line',
      x: 0,
      y: 0,
      points: [uiStore.startPoint, controlPoint, finalPoint],
      style: { ...uiStore.persistentStyle },
      startConnection: this.startConnection,
      endConnection: this.endConnection,
    } as any);

    uiStore.endDrawing();
    this.resetToolState();
    uiStore.setActiveTool('select');
    return null;
  }

  private resetToolState() {
    this.startConnection = null;
    this.endConnection = null;
  }

  protected validateElement(context: ToolContext): boolean {
    const { startPoint, currentPoint } = context;

    const dx = Math.abs(currentPoint.x - startPoint.x);
    const dy = Math.abs(currentPoint.y - startPoint.y);
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Allow creating zero-length lines if they are connected
    if (distance < 5 && (this.startConnection || this.endConnection)) {
      return true;
    }

    return distance >= 5;
  }

  getCursor(): string {
    return 'crosshair';
  }
}
